import { useMemo, useState } from 'react'
import { UserPlus, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../lib/shadcn/dialog'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useAllocateDriverToSeat, useMoveAllocationSeat } from '../../hooks/backend/fleet'
import { type VehicleGroup, type EligibleDriver, type BenchDriver } from '../../utils/allocation'

// The picker value encodes which group a driver came from, because the two
// groups are written by different functions: a bench driver is seated with
// allocate_driver_to_seat, a currently-seated driver is moved with
// move_allocation_seat.
const BENCH = 'bench'
const SEATED = 'seated'

// Manual allocation: fill one of this vehicle's empty seats with an eligible
// driver. The picker offers two groups, defaulting to the bench: drivers who
// hold no seat this run (placed with allocate_driver_to_seat), and eligible
// drivers who currently hold a seat elsewhere (moved with move_allocation_seat).
// The database is the final guard: it refuses an ineligible driver, a taken
// seat, or a driver who already holds a seat that week, and that message is
// surfaced verbatim.
export function AllocateDriverDialog({
  vehicle,
  benchDrivers,
  seatedDrivers,
  onAllocated,
}: {
  vehicle: VehicleGroup
  benchDrivers: BenchDriver[]
  seatedDrivers: EligibleDriver[]
  onAllocated: () => void
}) {
  const [open, setOpen] = useState(false)
  const [targetShift, setTargetShift] = useState<string>('')
  // Encoded as "bench:<id>" or "seated:<id>".
  const [pick, setPick] = useState<string>('')
  const [reason, setReason] = useState('')
  const allocate = useAllocateDriverToSeat()
  const move = useMoveAllocationSeat()

  const emptySeats = useMemo(
    () => vehicle.seats.filter((s) => s.is_empty_slot),
    [vehicle.seats],
  )

  const sortedBench = useMemo(
    () => [...benchDrivers].sort((a, b) => (a.driver_name ?? '').localeCompare(b.driver_name ?? '')),
    [benchDrivers],
  )

  // Seated eligible drivers, excluding any already on this vehicle.
  const sortedSeated = useMemo(
    () =>
      seatedDrivers
        .filter((d) => d.current_vehicle_id !== vehicle.vehicle_id)
        .sort((a, b) => (a.driver_name ?? '').localeCompare(b.driver_name ?? '')),
    [seatedDrivers, vehicle.vehicle_id],
  )

  const reasonValid = reason.trim().length > 0
  const submitting = allocate.loading || move.loading
  const canSubmit = targetShift !== '' && pick !== '' && reasonValid && !submitting

  function reset() {
    setTargetShift(emptySeats[0] ? String(emptySeats[0].shift_index) : '')
    setPick('')
    setReason('')
  }

  async function handleAllocate() {
    if (!canSubmit) return
    const [group, driverId] = pick.split(':')
    const trimmedReason = reason.trim()
    try {
      if (group === BENCH) {
        await allocate.trigger({
          run_id: vehicle.run_id,
          vehicle_id: vehicle.vehicle_id,
          shift_index: Number(targetShift),
          driver_id: driverId,
          reason: trimmedReason,
        }).result
      } else {
        const seated = sortedSeated.find((d) => d.driver_id === driverId)
        if (!seated) return
        await move.trigger({
          seat_id: seated.seat_id,
          new_vehicle_id: vehicle.vehicle_id,
          new_shift_index: Number(targetShift),
          reason: trimmedReason,
        }).result
      }
      toast.success('Driver allocated', {
        description: `Seat filled on ${vehicle.plate}.`,
      })
      setOpen(false)
      reset()
      onAllocated()
    } catch (err) {
      // Surface the message the RPC or eligibility trigger raised, verbatim
      // (for example "Cannot allocate ...", a taken seat, or an already-seated
      // driver).
      toast.error('Allocation not done', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  const noEmptySeats = emptySeats.length === 0
  const mutationError = allocate.error || move.error

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) reset()
        setOpen(next)
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-full gap-1.5 px-2 text-xs"
          disabled={noEmptySeats}
          title={noEmptySeats ? 'This vehicle has no empty seats.' : undefined}
        >
          <UserPlus className="h-3.5 w-3.5" />
          Allocate driver
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Allocate a driver</DialogTitle>
          <DialogDescription>
            Fill an empty seat on {vehicle.plate}. The bench lists drivers with no seat this week;
            you can also move an eligible driver who is currently seated elsewhere. A benched driver
            is shown with his reason and cannot be picked until the bench is released.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="allocate-seat">Empty seat</Label>
            <Select value={targetShift} onValueChange={setTargetShift}>
              <SelectTrigger id="allocate-seat">
                <SelectValue placeholder="Choose a seat" />
              </SelectTrigger>
              <SelectContent>
                {emptySeats.map((s) => (
                  <SelectItem key={s.shift_index} value={String(s.shift_index)}>
                    {s.seat_label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="allocate-driver">Driver</Label>
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger id="allocate-driver">
                <SelectValue placeholder="Choose a driver" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Bench, no seat this week</SelectLabel>
                  {sortedBench.length === 0 ? (
                    <SelectItem value="bench-none" disabled>
                      No bench drivers available
                    </SelectItem>
                  ) : (
                    sortedBench.map((d) => (
                      // A benched driver stays listed and is not selectable, with the
                      // reason on the row. Dropping him from the list instead would
                      // leave a manager wondering where he went, which is how the last
                      // three defects on this screen stayed invisible.
                      <SelectItem
                        key={`bench-${d.driver_id}`}
                        value={`${BENCH}:${d.driver_id}`}
                        disabled={d.benched === true}
                      >
                        {d.benched
                          ? (d.driver_name || 'Unnamed driver') +
                            ', benched: ' +
                            (d.bench_reason || 'no reason recorded')
                          : d.driver_name || 'Unnamed driver'}
                      </SelectItem>
                    ))
                  )}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Currently seated elsewhere</SelectLabel>
                  {sortedSeated.length === 0 ? (
                    <SelectItem value="seated-none" disabled>
                      No eligible seated drivers to move
                    </SelectItem>
                  ) : (
                    sortedSeated.map((d) => (
                      <SelectItem key={`seated-${d.driver_id}`} value={`${SEATED}:${d.driver_id}`}>
                        {(d.driver_name || 'Unnamed driver') +
                          ', now on ' +
                          (d.current_plate || 'another car') +
                          ' Seat ' +
                          d.current_shift_index}
                      </SelectItem>
                    ))
                  )}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="allocate-reason">Reason</Label>
            <Textarea
              id="allocate-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this driver being allocated here? A driver must be able to be told."
              rows={3}
            />
            {!reasonValid && reason.length > 0 && (
              <p className="text-xs text-destructive">A reason cannot be blank.</p>
            )}
          </div>

          {mutationError && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{mutationError}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleAllocate} disabled={!canSubmit} className="gap-1.5">
            {submitting ? <Spinner className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            Allocate driver
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
